// Reusable bits for any list view that wants clickable-header sorting and a
// Filter popover. Each table defines its own SORT_EXTRACT map (key → fn(row))
// and its own FILTER_SPEC (list of filter fields); the rendering primitives
// here are generic.
//
// Usage:
//   const [sortKey, setSortKey] = React.useState('updated');
//   const [sortDir, setSortDir] = React.useState('desc');
//   const toggleSort = window.makeToggleSort(setSortKey, setSortDir);
//   const rows = React.useMemo(() => {
//     let r = applyFilters(items, filters);
//     return sortKey ? r.slice().sort(makeComparator(sortKey, sortDir, EXTRACT)) : r;
//   }, [items, filters, sortKey, sortDir]);
//
//   <SortableHeader col={c} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} extractors={EXTRACT} />

// ────────────────────────────────────────────────────────────────────────
// Comparators

// Given a column key, sort direction, and an extractor map keyed by column,
// returns a comparator fn (a, b) → number. Nulls always sink, regardless of
// direction — empty cells rising to the top of a desc sort is visual noise
// nobody wants.
window.makeComparator = (key, dir, extractors, ctx) => {
  const extract = extractors[key];
  if (!extract) return () => 0;
  const sign = dir === 'desc' ? -1 : 1;
  return (a, b) => {
    const va = extract(a, ctx);
    const vb = extract(b, ctx);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    if (va < vb) return -1 * sign;
    if (va > vb) return  1 * sign;
    return 0;
  };
};

// Factory that returns the toggleSort callback used by every clickable
// header. Same column → flip asc/desc; new column → start ascending.
window.makeToggleSort = (setSortKey, setSortDir) => (key) => {
  setSortKey((prev) => {
    if (prev !== key) { setSortDir('asc'); return key; }
    setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    return key;
  });
};

// ────────────────────────────────────────────────────────────────────────
// Per-user UI preferences (105). One GET on boot holds every view's layout;
// writes are debounced and mirrored to localStorage so a reload paints the
// user's layout immediately instead of flashing the stock one while the
// fetch is in flight.
//
// The server blob is frontend-owned and opaque to the API:
//   { columns: { order: [...visibleKeys], hidden: [...keys], widths: {key:px} },
//     sort: { key, dir: 'asc' | 'desc' },     // header-click sort, per user
//     mode: 'table' | 'cards' }

const _PREFS_LS = 'atlas.uiprefs';       // { [viewKey]: prefsBlob } mirror
let _prefsPromise = null;                // in-flight/settled boot GET
let _prefsMem = null;                    // resolved { [viewKey]: prefsBlob }
let _touchPrefs = {};                    // touch-device edits, this page load only (see _saveUiPrefs)

// What a view should show: on a touch device, this page load's edit if there
// is one; otherwise the saved layout from `all` (the server copy or its
// mirror). A mouse/desktop session never reads the touch edits, even on a
// 2-in-1 that was in tablet mode earlier in this page load.
const _viewPrefs = (all, viewKey) => (
  (window.AtlasDevice && window.AtlasDevice.touch && Object.prototype.hasOwnProperty.call(_touchPrefs, viewKey))
    ? _touchPrefs[viewKey] : ((all || {})[viewKey] || null)
);

const _readPrefsLS = () => {
  try { const s = JSON.parse(localStorage.getItem(_PREFS_LS) || '{}'); return (s && typeof s === 'object') ? s : {}; }
  catch (_) { return {}; }
};
const _writePrefsLS = (all) => { try { localStorage.setItem(_PREFS_LS, JSON.stringify(all)); } catch (_) {} };

// Resolves once per page load. A failure (offline, 401 before sign-in) is not
// fatal — fall back to the localStorage mirror so layouts still apply.
window.loadUiPrefs = () => {
  if (_prefsPromise) return _prefsPromise;
  _prefsPromise = Promise.resolve()
    .then(() => (window.AtlasApi && window.AtlasApi.uiPrefs ? window.AtlasApi.uiPrefs.list() : []))
    .then((rows) => {
      const all = {};
      for (const r of (rows || [])) if (r && r.view_key) all[r.view_key] = r.prefs || {};
      _prefsMem = all;
      _writePrefsLS(all);
      return all;
    })
    .catch(() => { _prefsMem = _readPrefsLS(); return _prefsMem; });
  return _prefsPromise;
};

// Sign-out / user switch must not leak one person's layout into the next
// session's first paint.
window.clearUiPrefsCache = () => {
  _prefsPromise = null; _prefsMem = null; _touchPrefs = {};
  try { localStorage.removeItem(_PREFS_LS); } catch (_) {}
};

const _saveTimers = {};
const _saveUiPrefs = (viewKey, blob) => {
  // There is ONE saved layout per view and the desktop reads it. A phone or
  // iPad (any touch device, including one switched to the desktop layout)
  // hides and reorders columns to fit a small screen, so its edits must
  // never reach that copy: they apply until the next reload (_touchPrefs,
  // so leaving the page and coming back keeps them) and are never written
  // to the server or the localStorage mirror.
  //
  // Why not let an iPad remember its layout in the mirror: the boot GET
  // overwrites the mirror with the server copy on every load (so the iPad
  // layout would flash, then snap back), and when that GET fails the mirror
  // becomes the base the next save builds on, which would push the iPad
  // layout to the server. A real per-device layout needs its own key.
  if (window.AtlasDevice && window.AtlasDevice.touch) { _touchPrefs[viewKey] = blob; return; }
  const all = _prefsMem || _readPrefsLS();
  all[viewKey] = blob;
  _prefsMem = all;
  _writePrefsLS(all);                                  // instant, survives reload
  clearTimeout(_saveTimers[viewKey]);                  // coalesce drag storms
  _saveTimers[viewKey] = setTimeout(() => {
    if (!window.AtlasApi || !window.AtlasApi.uiPrefs) return;
    window.AtlasApi.uiPrefs.save(viewKey, blob).catch(() => {
      // Keep the local mirror; the layout still works for this browser and
      // the next successful save reconciles. Nagging a toast on every failed
      // column drag would be worse than silently degrading.
    });
  }, 500);
};

// ────────────────────────────────────────────────────────────────────────
// Column catalog → visible columns, with per-user show/hide/reorder/resize.
//
// A view declares ONE catalog — the single source of truth that replaces the
// three parallel lists every grid used to keep in sync (a fit spec, a header
// array, and hand-written body cells in matching order):
//
//   [{ key, label,
//      render(row, ctx) -> JSX,     // the cell; MUST size itself to ctx.w
//      accessor?(row) -> string,    // text sample for width fitting
//      align?: 'right', extra?: px, // fitting hints
//      def?: bool,                  // in the default layout (default true)
//      fixed?: bool,                // cannot be hidden (row identity)
//      group?: string, desc?: string }]  // Columns-menu grouping/tooltip
//
// Returns { cols, widths, startResize, menu } where `cols` are the visible
// entries in the user's order, each carrying a resolved `w` and `last`.
// Header and body both map over `cols`, so they cannot drift apart again.
//
// The header-click sort can ride the same blob: `sort` is the saved
// { key, dir } (or `opts.defaultSort`, or null = server order) and
// `toggleSort(key)` has makeToggleSort semantics (same column flips, new
// column starts ascending) but writes through, so the order a user picks
// follows them to the next visit and the next browser. Views that want a
// sort that resets each visit keep using their own state instead.
//
// Layout resolution is deliberately three-state so a later release adding a
// column doesn't have to guess: a key in `order` is shown, a key in `hidden`
// is hidden, and a key in NEITHER is new since the user last saved — it falls
// back to its `def` flag and lands next to its catalog neighbour.
window.useColumnPrefs = (viewKey, catalog, opts) => {
  const o = opts || {};
  const rows = o.rows || [];

  const [saved, setSaved] = React.useState(() => _viewPrefs(_prefsMem || _readPrefsLS(), viewKey));
  const [ready, setReady] = React.useState(() => _prefsMem != null);

  // Reconcile with the server once the boot GET lands. Skip if the user has
  // already touched the menu (`dirty`) — their in-flight edit outranks a
  // response that was issued before it.
  const dirty = React.useRef(false);
  React.useEffect(() => {
    let alive = true;
    window.loadUiPrefs().then((all) => {
      if (!alive) return;
      if (!dirty.current) setSaved(_viewPrefs(all, viewKey));
      setReady(true);
    });
    return () => { alive = false; };
  }, [viewKey]);

  const colBlob = (saved && saved.columns) || null;

  // ── Which columns, in what order
  const cols = React.useMemo(() => {
    const byKey = new Map(catalog.map((c) => [c.key, c]));
    const hidden = new Set((colBlob && colBlob.hidden) || []);
    const order  = (colBlob && Array.isArray(colBlob.order)) ? colBlob.order : null;
    if (!order) return catalog.filter((c) => c.def !== false && !hidden.has(c.key));

    const out = [];
    for (const k of order) {
      const c = byKey.get(k);                       // stale key from a removed
      if (c && !hidden.has(k)) out.push(c);         // column: drop, don't crash
    }
    // Columns this saved layout predates. Insert each after its nearest
    // already-placed catalog predecessor so a new column appears where the
    // developer put it, not tacked onto the end.
    const known = new Set([...order, ...hidden]);
    catalog.forEach((c, ci) => {
      if (known.has(c.key) || c.def === false) return;
      let at = 0;
      for (let i = ci - 1; i >= 0; i--) {
        const prev = out.findIndex((x) => x.key === catalog[i].key);
        if (prev >= 0) { at = prev + 1; break; }
      }
      out.splice(at, 0, c);
    });
    return out;
  }, [catalog, colBlob]);

  // ── Widths: fitted defaults, overridden by anything the user has dragged
  const fitDefaults = React.useMemo(
    () => window.fitColWidths(catalog, rows, { max: o.fitMax || 400 }),
    [catalog, rows],
  );

  // One-time carry-over of widths dragged before this feature existed, so
  // nobody's tuned layout resets on the release that adds the Columns button.
  const legacyWidths = React.useMemo(() => {
    if (colBlob && colBlob.widths) return null;
    try { return JSON.parse(localStorage.getItem('atlas.colw.' + (o.legacyWidthKey || viewKey)) || 'null'); }
    catch (_) { return null; }
  }, [viewKey, colBlob]);

  const widthOverrides = (colBlob && colBlob.widths) || legacyWidths || {};
  const widths = React.useMemo(
    () => ({ ...fitDefaults, ...widthOverrides }),
    [fitDefaults, JSON.stringify(widthOverrides)],
  );

  // ── Writes
  const write = React.useCallback((mutate) => {
    dirty.current = true;
    setSaved((prev) => {
      const base = prev || {};
      const next = mutate({
        ...base,
        columns: { order: null, hidden: [], widths: {}, ...(base.columns || {}) },
      });
      _saveUiPrefs(viewKey, next);
      return next;
    });
  }, [viewKey]);

  // `order` is only meaningful as a complete visible list, so materialize the
  // currently-resolved order the first time the user changes anything.
  const currentOrder = cols.map((c) => c.key);

  const api = React.useMemo(() => ({
    toggle: (key) => write((next) => {
      const c = catalog.find((x) => x.key === key);
      if (c && c.fixed) return next;                     // identity column stays
      // Visibility comes from the RESOLVED column list, not from the hidden
      // set: a `def:false` column the user has never touched is in neither
      // `order` nor `hidden`, so asking "is it hidden?" would answer no and
      // turn a show-me click into a hide.
      const isVisible = currentOrder.includes(key);
      const hidden = new Set(next.columns.hidden || []);
      const order = (next.columns.order || currentOrder).slice();
      if (isVisible) {
        hidden.add(key);
        return { ...next, columns: { ...next.columns, hidden: [...hidden], order: order.filter((k) => k !== key) } };
      }
      hidden.delete(key);
      if (!order.includes(key)) {
        // Insert at its catalog position rather than at the far right, so the
        // grid keeps the shape the developer laid out.
        const ci = catalog.findIndex((x) => x.key === key);
        let at = order.length;
        for (let i = ci + 1; i < catalog.length; i++) {
          const nx = order.indexOf(catalog[i].key);
          if (nx >= 0) { at = nx; break; }
        }
        order.splice(at, 0, key);
      }
      return { ...next, columns: { ...next.columns, hidden: [...hidden], order } };
    }),

    move: (key, toIndex) => write((next) => {
      const order = (next.columns.order || currentOrder).filter((k) => k !== key);
      order.splice(Math.max(0, Math.min(order.length, toIndex)), 0, key);
      return { ...next, columns: { ...next.columns, order } };
    }),

    setWidth: (key, px) => write((next) => ({
      ...next, columns: { ...next.columns, widths: { ...(next.columns.widths || {}), [key]: px } },
    })),

    reset: () => {
      dirty.current = true;
      setSaved((prev) => {
        const next = { ...(prev || {}) };
        delete next.columns;
        _saveUiPrefs(viewKey, next);
        return next;
      });
    },

    // View-level mode (Notes' table/cards toggle) rides the same blob.
    setMode: (mode) => write((next) => ({ ...next, mode })),

    // Persisted header sort. `setSort(null)` goes back to the default order.
    setSort: (key, dir) => write((next) => {
      const out = { ...next };
      if (key) out.sort = { key, dir: dir === 'desc' ? 'desc' : 'asc' };
      else delete out.sort;
      return out;
    }),
    toggleSort: (key) => write((next) => {
      const cur = next.sort || o.defaultSort || null;
      const dir = cur && cur.key === key && cur.dir === 'asc' ? 'desc' : 'asc';
      return { ...next, sort: { key, dir } };
    }),
  }), [write, catalog, JSON.stringify(currentOrder), JSON.stringify(o.defaultSort || null)]);

  // ── Drag-to-resize, writing through to the same blob
  const drag = React.useRef(null);
  const [dragW, setDragW] = React.useState(null);       // live width during drag
  React.useEffect(() => {
    const onMove = (e) => {
      const d = drag.current; if (!d) return;
      setDragW({ key: d.key, w: Math.max(56, Math.min(900, d.startW + (e.clientX - d.startX))) });
    };
    const onUp = () => {
      const d = drag.current; if (!d) return;
      drag.current = null;
      document.body.style.userSelect = ''; document.body.style.cursor = '';
      setDragW((live) => { if (live) api.setWidth(live.key, live.w); return null; });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [api]);

  const startResize = (key) => (e) => {
    e.preventDefault(); e.stopPropagation();
    drag.current = { key, startX: e.clientX, startW: widths[key] || 120 };
    document.body.style.userSelect = 'none'; document.body.style.cursor = 'col-resize';
  };

  const liveWidths = dragW ? { ...widths, [dragW.key]: dragW.w } : widths;

  // Resolved columns, ready to map in both the header and the body.
  const resolved = cols.map((c, i) => ({
    ...c, w: liveWidths[c.key], last: i === cols.length - 1,
  }));

  return {
    cols: resolved,
    widths: liveWidths,
    startResize,
    ready,
    mode: (saved && saved.mode) || o.defaultMode || 'table',
    sort: (saved && saved.sort && saved.sort.key) ? saved.sort : (o.defaultSort || null),
    hiddenCount: catalog.filter((c) => c.def !== false).length - cols.length,
    ...api,
  };
};

// ────────────────────────────────────────────────────────────────────────
// Admin-defined formula/AI fields (FieldDefinition) as catalog entries, so a
// custom column hides, reorders and resizes like a built-in one. They used to
// render as a fixed tail appended after the catalog in both the header and
// the row — visible always, movable never.
//
// Keys are namespaced `f:<field_key>`: a custom field called "owner" must not
// collide with the built-in owner column, and a saved layout naming a field
// that has since been deleted is dropped by the normal stale-key path.
//
// Deliberately not sortable. The values are computed per row in Python, so
// there is no ORDER BY for them; a header click could only rank the pages
// already loaded and present that as the whole set — the same trap the
// engagements sort registry exists to avoid.
window.formulaColumns = (defs) => (defs || []).map((def) => {
  const isNum = def.field_type === 'number' || def.field_type === 'currency';
  const formula = (def.field_config || {}).formula || '';
  return {
    key: 'f:' + def.field_key,
    xkey: def.field_key,          // exports without the namespace prefix
    label: def.display_name,
    group: 'Custom fields',
    desc: formula ? `Formula · ${formula}` : `Custom field · ${def.field_key}`,
    align: isNum ? 'right' : undefined,
    icon: <Icon.Lightning size={10} />,
    headerColor: 'var(--accent-text)',
    accessor: (row) => {
      const v = (row.computed || {})[def.field_key];
      return v == null ? '' : String(formatComputed(def, v));
    },
    render: (row, ctx, col) => {
      const v = (row.computed || {})[def.field_key];
      const missing = v == null ? missingFormulaRefs(def, row._raw) : [];
      const tooltip = missing.length
        ? `Missing: ${missing.join(', ')}\n\nFormula: ${formula}`
        : formula;
      return (
        <div key={col.key} title={tooltip} className={isNum ? 'num' : ''}
          style={{
            width: col.w, padding: '0 10px', display: 'flex', alignItems: 'center', gap: 5,
            justifyContent: isNum ? 'flex-end' : 'flex-start',
            fontSize: 12, color: v == null ? 'var(--text-4)' : 'var(--text-2)',
            borderRight: col.last ? 'none' : '1px solid var(--border)',
          }}>
          {missing.length > 0 && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 14, height: 14, borderRadius: 4, fontSize: 9,
              background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontWeight: 700,
            }}>▴</span>
          )}
          {formatComputed(def, v)}
        </div>
      );
    },
  };
});

// ────────────────────────────────────────────────────────────────────────
// The "Columns" button + popover: check to show/hide, drag the grip to
// reorder. Driven entirely by what useColumnPrefs returns.
window.ColumnsMenu = ({ catalog, cols, toggle, move, reset, label = 'Columns' }) => {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState('');
  const [dragKey, setDragKey] = React.useState(null);
  const [overIdx, setOverIdx] = React.useState(null);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const visible = new Set(cols.map((c) => c.key));
  const hiddenCols = catalog.filter((c) => !visible.has(c.key));
  const match = (c) => !q || (c.label || c.key).toLowerCase().includes(q.toLowerCase());
  const shown = cols.filter(match);
  const available = hiddenCols.filter(match);

  // Group the available list so a 30-entry catalog stays scannable.
  const groups = [];
  for (const c of available) {
    const g = c.group || 'More fields';
    let bucket = groups.find((x) => x.name === g);
    if (!bucket) { bucket = { name: g, items: [] }; groups.push(bucket); }
    bucket.items.push(c);
  }

  const onGripDown = (key) => (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragKey(key);
    document.body.style.userSelect = 'none';
  };

  React.useEffect(() => {
    if (!dragKey) return;
    const onUp = () => {
      if (overIdx != null) move(dragKey, overIdx);
      setDragKey(null); setOverIdx(null);
      document.body.style.userSelect = '';
    };
    window.addEventListener('mouseup', onUp);
    return () => window.removeEventListener('mouseup', onUp);
  }, [dragKey, overIdx, move]);

  const row = (c, i, isShown) => (
    <div key={c.key}
      onMouseEnter={() => { if (dragKey && isShown) setOverIdx(i); }}
      style={{
        display: 'flex', alignItems: 'center', gap: 7, padding: '5px 6px',
        borderRadius: 6, fontSize: 12.5, color: 'var(--text)',
        background: dragKey === c.key ? 'var(--surface-hover)' : 'transparent',
        borderTop: (dragKey && isShown && overIdx === i && dragKey !== c.key)
          ? '2px solid var(--accent)' : '2px solid transparent',
        opacity: dragKey && dragKey !== c.key ? 0.75 : 1,
      }}>
      {isShown ? (
        <span onMouseDown={onGripDown(c.key)} title="Drag to reorder"
          style={{ cursor: 'grab', color: 'var(--text-4)', fontSize: 11, lineHeight: 1, padding: '0 1px', userSelect: 'none' }}>⠿</span>
      ) : <span style={{ width: 9 }} />}
      <input type="checkbox" checked={isShown} disabled={Boolean(c.fixed)}
        onChange={() => toggle(c.key)}
        title={c.fixed ? 'This column identifies the row and cannot be hidden' : undefined}
        style={{ accentColor: 'var(--accent)', cursor: c.fixed ? 'not-allowed' : 'pointer' }} />
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {c.label}
      </span>
      {c.desc && (
        <span title={c.desc} style={{ fontSize: 10, color: 'var(--text-4)', cursor: 'help' }}>ⓘ</span>
      )}
    </div>
  );

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <Btn size="sm" variant="outline" onClick={() => setOpen((v) => !v)}>
        {label}{hiddenCols.length ? ` (${cols.length})` : ''}
      </Btn>
      {open && (
        <div style={{
          position: 'absolute', top: 30, right: 0, zIndex: 60, width: 268,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 8, boxShadow: 'var(--shadow-lg)', padding: 10,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Columns
            </span>
            <button onClick={() => { reset(); setQ(''); }}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--accent-text)' }}>
              Reset
            </button>
          </div>

          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a field…"
            style={{
              width: '100%', padding: '5px 8px', fontSize: 12, marginBottom: 8,
              border: '1px solid var(--border)', borderRadius: 5,
              background: 'var(--surface)', fontFamily: 'inherit',
            }} />

          <div style={{ maxHeight: 340, overflowY: 'auto' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '2px 6px 4px' }}>
              Shown · {cols.length}
            </div>
            {shown.map((c, i) => row(c, i, true))}
            {/* Drop target for "move to the very end" — the last row's own
                top-border zone can't express it. */}
            {dragKey && (
              <div onMouseEnter={() => setOverIdx(cols.length)}
                style={{ height: 8, borderTop: overIdx === cols.length ? '2px solid var(--accent)' : '2px solid transparent' }} />
            )}

            {groups.map((g) => (
              <div key={g.name}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '8px 6px 4px' }}>
                  {g.name}
                </div>
                {g.items.map((c) => row(c, -1, false))}
              </div>
            ))}

            {!shown.length && !available.length && (
              <div style={{ fontSize: 11.5, color: 'var(--text-4)', padding: '10px 6px' }}>
                No field matches “{q}”.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Resizable + auto-fitting columns. A view calls useColWidths('companies',
// defaults) where `defaults` are content-fitted widths (see fitColWidths). The
// hook returns live widths = { ...defaults, ...userOverrides } so columns
// auto-size to the data on first view, yet a column the user has dragged sticks
// (only the overridden keys persist to localStorage — defaults stay free to
// re-fit as data changes). Header AND cells read widths[key], in lockstep.
window.useColWidths = (storageKey, defaults) => {
  const lsKey = 'atlas.colw.' + storageKey;
  const [overrides, setOverrides] = React.useState(() => {
    try { const s = JSON.parse(localStorage.getItem(lsKey) || '{}'); return (s && typeof s === 'object') ? s : {}; } catch (_) { return {}; }
  });
  const widths = React.useMemo(() => ({ ...(defaults || {}), ...overrides }), [JSON.stringify(defaults), overrides]);
  const drag = React.useRef(null);   // { key, startX, startW }
  React.useEffect(() => {
    const onMove = (e) => {
      const d = drag.current; if (!d) return;
      const next = Math.max(56, Math.min(900, d.startW + (e.clientX - d.startX)));
      setOverrides((o) => ({ ...o, [d.key]: next }));
    };
    const onUp = () => {
      if (!drag.current) return;
      drag.current = null; document.body.style.userSelect = ''; document.body.style.cursor = '';
      setOverrides((o) => { try { localStorage.setItem(lsKey, JSON.stringify(o)); } catch (_) {} return o; });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [lsKey]);
  const startResize = (key) => (e) => {
    e.preventDefault(); e.stopPropagation();
    drag.current = { key, startX: e.clientX, startW: widths[key] || (defaults || {})[key] || 120 };
    document.body.style.userSelect = 'none'; document.body.style.cursor = 'col-resize';
  };
  return { widths, startResize };
};

// Content-fit column widths via canvas text measurement, so nothing is cut off
// on first view. `cols` = [{ key, label, accessor?(row)->string, extra? }];
// returns { key: px } (header label vs. widest sampled cell, clamped, + padding
// + an `extra` allowance for cells with a leading logo/avatar/pill).
const _fitCtx = (typeof document !== 'undefined') ? document.createElement('canvas').getContext('2d') : null;
window.fitColWidths = (cols, rows, opts) => {
  const o = opts || {};
  if (!_fitCtx) return Object.fromEntries((cols || []).map((c) => [c.key, c.def || 140]));
  const fam = (typeof getComputedStyle !== 'undefined' && document.body) ? getComputedStyle(document.body).fontFamily : 'sans-serif';
  const min = o.min || 80, max = o.max || 460, pad = o.pad != null ? o.pad : 26;
  const sample = (rows || []).slice(0, 200);
  const out = {};
  for (const c of cols) {
    _fitCtx.font = '600 11px ' + fam;                         // header (uppercase + sort caret)
    let w = _fitCtx.measureText(String(c.label || c.key).toUpperCase()).width + 22;
    _fitCtx.font = '12.5px ' + fam;
    for (const r of sample) {
      let v; try { v = c.accessor ? c.accessor(r) : r[c.key]; } catch (_) { v = null; }
      if (v == null || v === '') continue;
      const tw = _fitCtx.measureText(String(v)).width + (c.extra || 0);
      if (tw > w) w = tw;
    }
    out[c.key] = Math.round(Math.max(min, Math.min(max, w + pad)));
  }
  return out;
};

// ────────────────────────────────────────────────────────────────────────
// Filter utilities

// activeFilterCount / filterValueActive live with the FilterPopover below.

// ────────────────────────────────────────────────────────────────────────
// Sortable header cell. Renders the same chrome as the legacy non-sortable
// headers but flips background + adds ↑/↓ when active. Passing `sortable={false}`
// (or omitting `onSort`) renders a plain non-interactive header.
window.SortableHeader = ({ col, sortKey, sortDir, onSort, sortable = true, onResizeStart }) => {
  const canSort = sortable && Boolean(onSort);
  const isActive = canSort && sortKey === col.key;
  return (
    <div style={{
      width: col.w, flex: col.flex,
      padding: 0, display: 'flex', alignItems: 'stretch', position: 'relative',
      borderRight: col.last ? 'none' : '1px solid var(--border)',
    }}>
      {/* Touch: the 7px mouse-drag strip can't be grabbed with a finger and
          only eats taps meant for the sort button beside it — no handle. */}
      {onResizeStart && !(window.AtlasDevice && window.AtlasDevice.touch) && (
        <div onMouseDown={onResizeStart} title="Drag to resize column"
          style={{ position: 'absolute', right: -3, top: 0, bottom: 0, width: 7, cursor: 'col-resize', zIndex: 2 }} />
      )}
      <button
        onClick={canSort ? () => onSort(col.key) : undefined}
        disabled={!canSort}
        style={{
          width: '100%', padding: '0 10px', height: '100%', border: 'none',
          background: isActive ? 'var(--surface-hover)' : 'transparent',
          display: 'flex', alignItems: 'center', gap: 4,
          justifyContent: col.align === 'right' ? 'flex-end' : 'flex-start',
          textTransform: 'uppercase', letterSpacing: '0.04em',
          cursor: canSort ? 'pointer' : 'default',
          // headerColor marks a column that isn't a stock field (today: the
          // admin-defined formula/AI columns, tinted accent).
          color: isActive ? 'var(--text)' : (col.headerColor || 'var(--text-3)'),
          fontSize: 11, fontWeight: 500, fontFamily: 'inherit',
          transition: 'background .1s',
        }}
        onMouseEnter={(e) => { if (canSort && !isActive) e.currentTarget.style.background = 'var(--surface-hover)'; }}
        onMouseLeave={(e) => { if (canSort && !isActive) e.currentTarget.style.background = 'transparent'; }}
      >
        {col.icon}
        {col.label}
        {isActive && (
          <span style={{ fontSize: 9, opacity: 0.7 }}>
            {sortDir === 'desc' ? '↓' : '↑'}
          </span>
        )}
      </button>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Record created / last-modified cell, shared by every grid that shows them
// (engagements, companies, people, referrals, GPP partners) so one page can't
// drift into reading a different timestamp than another.
//
// `source` picks which timestamps are authoritative for the row's table:
//   'dc'    — DealCloud-mirrored (default): dc_created_at / dc_modified_at.
//             Atlas created_at/updated_at describe the SYNC on these tables,
//             never the record (see the note in data.jsx), so they're only a
//             last-resort fallback for created, rendered dimmer + labelled.
//   'atlas' — Atlas owns the table (GPP partners): created_at / updated_at
//             are the record's real dates, so they render normally.
//
// Created shows an absolute date (you scan it against other dates); modified
// shows "6d ago" like the existing Updated columns, full date in the tooltip.
// `border` draws the cell's right divider — grids differ (companies rules its
// columns, deals doesn't), so the caller opts in rather than the cell guessing.
window.RecordDateCell = ({ row, kind = 'created', source = 'dc', w, border = false, last = false, inline = false }) => {
  const atlasSource = source === 'atlas';
  const iso = kind === 'created'
    ? (atlasSource ? (row && row.created_at) || null : window.recordCreated(row))
    : (atlasSource ? (row && row.updated_at) || null : window.recordModified(row));
  const fromAtlas = !atlasSource && kind === 'created' && window.recordCreatedIsAtlas(row);
  const full = iso ? new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit',
  }) : null;
  // Recent modifications read better relative ("2d ago"); past ~2 weeks that
  // stops meaning anything, so switch to the same absolute format the Created
  // column uses — relativeTime's own fallback is m/d/yyyy, which looks like a
  // different column next to "Aug 18, 2024".
  const label = !iso ? '—'
    : kind === 'created' ? window.fmtDate(iso)
    : (Date.now() - new Date(iso).getTime()) < 14 * 86400e3
      ? window.relativeTime(iso)
      : window.fmtDate(iso);
  const title = !iso
    ? (kind === 'modified' && !atlasSource
        ? 'DealCloud reports no modified date for this record'
        : 'No date on this record')
    : kind === 'created'
      ? (fromAtlas
          ? `Created in Atlas ${full} — this record has no DealCloud created date`
          : `Created ${full}${atlasSource ? '' : ' (DealCloud)'}`)
      : `Last modified ${full}${atlasSource ? '' : ' (DealCloud)'}`;
  const text = (
    <span title={title} style={{
      whiteSpace: 'nowrap',
      color: !iso ? 'var(--text-4)' : fromAtlas ? 'var(--text-4)' : 'var(--text-3)',
      fontStyle: fromAtlas ? 'italic' : undefined,
    }}>{label}</span>
  );
  if (inline) return text;
  return (
    <div style={{
      width: w, padding: '0 10px', display: 'flex', alignItems: 'center', fontSize: 11.5,
      borderRight: border && !last ? '1px solid var(--border)' : 'none',
    }}>{text}</div>
  );
};

// Sort extractors for the two columns above — drop these into a view's
// SORT_EXTRACT map so both headers sort. Missing dates sort last (empty
// string), matching how the other optional-date columns behave.
window.recordDateSort = (source = 'dc') => ({
  created: (r) => (source === 'atlas'
    ? (r._raw || r).created_at
    : window.recordCreated(r._raw || r)) || '',
  updated: (r) => (source === 'atlas'
    ? (r._raw || r).updated_at
    : window.recordModified(r._raw || r)) || '',
});

const FILTER_INPUT_STYLE = { width: '100%', padding: '5px 8px', fontSize: 12,
  border: '1px solid var(--border)', borderRadius: 5, fontFamily: 'inherit' };

// Numeric filter input. Accepts the same shorthand as every other money
// field — "4M", "100k", "$1.5M", "4,000,000" — because this control used to
// strip everything but digits and dots, so a "4M" filter silently ran at 4.
//
// What lands in `filters` is always a plain numeric STRING ("4000000"), which
// is what the Number() / Number.isNaN guards in the views expect; the
// comma-grouped text is display only and lives here. Input that doesn't
// parse leaves the stored filter untouched rather than narrowing the grid to
// something nobody asked for.
const NumberFilterInput = ({ value, list, placeholder, onCommit }) => {
  const [raw, setRaw] = React.useState(value == null ? '' : String(value));
  // The last string we pushed upward, so an outside change (Clear all, a
  // saved view) re-seeds the box but our own writes don't clobber the text
  // the user is mid-way through typing.
  const pushed = React.useRef(value == null ? '' : String(value));
  React.useEffect(() => {
    const ext = value == null ? '' : String(value);
    if (ext === pushed.current) return;
    pushed.current = ext;
    setRaw(ext);
  }, [value]);

  const push = (v) => { pushed.current = v; onCommit(v); };
  const asStored = (n) => (n == null ? '' : String(n));
  const asShown  = (stored) => (stored === '' ? '' : window.formatNumInput(Number(stored)));

  return (
    <input
      value={raw} list={list} inputMode="decimal"
      onChange={(e) => {
        setRaw(e.target.value);
        const n = window.parseNumInput(e.target.value);
        if (n === undefined) return;   // mid-word: keep the last good filter
        push(asStored(n));
      }}
      onBlur={() => {
        const n = window.parseNumInput(raw);
        if (n === undefined) { setRaw(asShown(pushed.current)); return; }
        push(asStored(n));
        setRaw(asShown(asStored(n)));
      }}
      placeholder={placeholder}
      style={FILTER_INPUT_STYLE}
    />
  );
};

// ────────────────────────────────────────────────────────────────────────
// Filter builder popover.
//
// The previous popover laid out every field at once — a dozen controls,
// each with a paragraph under it, running past the bottom of the viewport
// with no way to scroll. This one opens on just the filters in use, as
// compact rows, plus an "Add filter" picker for everything else. Field
// explanations live in the row label's tooltip, never as body text.
//
// spec is an array of:
//   { key, label, type: 'chips'|'select'|'number'|'text'|'toggle'|'score',
//     options?, optionsAsync?, loadOptions?, onPick?, placeholder?, list?,
//     hint?, col? }
//
// - `chips`   — multi-select chip cluster; filters[key] is an array
// - `select`  — single pick; filters[key] is a string. loadOptions switches
//               it to the server-searchable Combobox (see below)
// - `number`  — numeric input, shorthand-aware ("4M" → "4000000");
//               filters[key] is a plain numeric string (caller coerces)
// - `text`    — free text; filters[key] is a string
// - `toggle`  — on/off; filters[key] is a boolean (adding the row turns it on)
// - `score`   — a 1–10 score plus a comparison. filters[key] is '' or
//               { n, op } with op 'eq' (exactly n — the default), 'lt'
//               (below n) or 'gte' (n and above). Picking a score alone
//               matches that score only; the two toggles widen it.
//
// hint: the row label's tooltip.
// col:  the grid column this field already covers, so the column list below
//       doesn't offer the same thing twice.
//
// optionsAsync: () => Promise<list> for lazily loaded option lists (team
// members, industries). Loaded once per popover open.
//
// loadOptions: (q) => Promise<[{value, label}]> — switches a `select` to the
// server-searchable Combobox (window.searchCompanyOptions et al) for option
// spaces too big to preload (312k companies). `options` entries stay pinned
// above the search results; pass the picked option back in there (via
// `onPick`) so the trigger still shows its label after the popover remounts.
//
// columns: optionally the grid's column catalog. Any column no spec field
// covers can be added as a filter too — stored under `col:<column key>` as
// { op, value }. Values come from the column's export getter (xget, else
// accessor), so they compare raw: numbers as numbers, dates as ISO strings.
// The kind is col.filterKind ('text' | 'number' | 'date'), else 'number'
// for right-aligned columns, else 'text'. Views apply them with
// window.applyColumnFilters(rows, filters, catalog) in their client-side
// pass — like every client filter, they only see the rows already loaded.

const COL_FILTER_PREFIX = 'col:';
window.COL_FILTER_PREFIX = COL_FILTER_PREFIX;

// Is a filter slot set? Arrays need items; strings/booleans need truthiness;
// the object-valued kinds (score, column) need a picked score, a typed value,
// or a presence-only operator.
window.filterValueActive = (v) => {
  if (v == null || v === '' || v === false) return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') {
    if (v.n != null) return true;
    if (v.op === 'empty' || v.op === 'not_empty') return true;
    return v.value != null && v.value !== '';
  }
  return Boolean(v);
};

// Counts how many filter slots are set. Drives the Filter button's badge.
window.activeFilterCount = (filters) => {
  let n = 0;
  for (const v of Object.values(filters || {})) if (window.filterValueActive(v)) n += 1;
  return n;
};

const COL_OPS = {
  text:   [['contains', 'contains'], ['not_contains', "doesn't contain"], ['is', 'is'],
           ['empty', 'is empty'], ['not_empty', 'is not empty']],
  number: [['eq', '='], ['lt', '<'], ['gte', '≥'],
           ['empty', 'is empty'], ['not_empty', 'is not empty']],
  date:   [['on', 'on'], ['before', 'before'], ['after', 'after'],
           ['empty', 'is empty'], ['not_empty', 'is not empty']],
};
const NO_VALUE_OPS = new Set(['empty', 'not_empty']);

window.columnFilterKind = (col) => col.filterKind || (col.align === 'right' ? 'number' : 'text');

const colFilterValue = (col, row) => {
  try { return col.xget ? col.xget(row) : col.accessor ? col.accessor(row) : null; }
  catch (_) { return null; }
};

// Does `row` pass one column filter { op, value } on `col`? A row whose
// value is missing never matches a comparison — only "is empty".
window.columnFilterMatch = (col, f, row) => {
  const kind = window.columnFilterKind(col);
  const raw = colFilterValue(col, row);
  const empty = raw == null || raw === '';
  if (f.op === 'empty') return empty;
  if (f.op === 'not_empty') return !empty;
  if (f.value == null || f.value === '') return true;   // operator picked, nothing typed yet
  if (kind === 'number') {
    if (empty) return false;
    const a = Number(raw), b = Number(f.value);
    if (Number.isNaN(a) || Number.isNaN(b)) return false;
    return f.op === 'lt' ? a < b : f.op === 'gte' ? a >= b : a === b;
  }
  if (kind === 'date') {
    if (empty) return false;
    const a = String(raw).slice(0, 10), b = String(f.value).slice(0, 10);
    return f.op === 'before' ? a < b : f.op === 'after' ? a > b : a === b;
  }
  const a = String(raw).toLowerCase(), b = String(f.value).toLowerCase();
  if (f.op === 'is') return a === b;
  if (f.op === 'not_contains') return !a.includes(b);
  return a.includes(b);
};

// Apply every `col:` filter in `filters` to `rows` using the view's catalog.
window.applyColumnFilters = (rows, filters, catalog) => {
  const active = Object.entries(filters || {})
    .filter(([k, v]) => k.startsWith(COL_FILTER_PREFIX) && window.filterValueActive(v))
    .map(([k, v]) => [(catalog || []).find((c) => c.key === k.slice(COL_FILTER_PREFIX.length)), v])
    .filter(([c]) => c);
  if (!active.length) return rows;
  return rows.filter((r) => active.every(([c, f]) => window.columnFilterMatch(c, f, r)));
};

const filterPillStyle = (active) => ({
  padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, cursor: 'pointer',
  background: active ? 'var(--accent-soft)' : 'var(--surface-2)',
  color:      active ? 'var(--accent-text)' : 'var(--text-2)',
  border:     active ? '1px solid var(--accent-text)' : '1px solid var(--border)',
});

// 1–10 score filter: the same ten-segment bar as the Hot score cell, lit
// over the scores that MATCH (one segment for "exactly", a run for the two
// range modes), a readout that says it in words, and the two range toggles.
// Clicking the picked score again clears it, like the cell does.
const ScoreFilterControl = ({ value, onChange }) => {
  const cur = value && typeof value === 'object' ? value : null;
  const n  = cur ? (cur.n ?? null) : null;
  const op = (cur && cur.op) || 'eq';
  const [preview, setPreview] = React.useState(null);
  const shown = preview ?? n;
  const band = (window.HUE && (window.HUE[window.hotScoreHue(shown)] || window.HUE.slate)) || { dot: 'var(--accent)' };
  const lit = (k) => shown != null && (op === 'lt' ? k < shown : op === 'gte' ? k >= shown : k === shown);
  const set = (patch) => onChange({ n, op, ...patch });
  const words = (k, o) => (k == null ? 'Pick a score'
    : o === 'lt' ? `Scores below ${k}` : o === 'gte' ? `Scores ${k} and above` : `Score ${k} only`);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span role="slider" aria-label="Hot score" aria-valuemin={1} aria-valuemax={10} aria-valuenow={n ?? undefined}
          style={{ display: 'inline-flex', gap: 3 }} onMouseLeave={() => setPreview(null)}>
          {Array.from({ length: 10 }, (_, i) => i + 1).map((k) => (
            <button key={k} type="button" title={k === n ? `${k} — click again to clear` : words(k, op)}
              onMouseEnter={() => setPreview(k)}
              onClick={() => set({ n: k === n ? null : k })}
              style={{
                width: 14, height: 20, padding: 0, cursor: 'pointer',
                border: `1px solid ${lit(k) ? band.dot : 'var(--border)'}`, borderRadius: 3,
                background: lit(k) ? band.dot : 'var(--surface)',
                transition: 'background .08s',
              }} />
          ))}
        </span>
        <span className="num" style={{ fontWeight: 600, minWidth: 18, textAlign: 'right',
          color: shown == null ? 'var(--text-4)' : 'var(--text)' }}>{shown ?? '—'}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6 }}>
        <button type="button" onClick={() => set({ op: op === 'lt' ? 'eq' : 'lt' })}
          title="Show scores below the picked one" style={filterPillStyle(op === 'lt')}>Less than</button>
        <button type="button" onClick={() => set({ op: op === 'gte' ? 'eq' : 'gte' })}
          title="Show the picked score and everything above it" style={filterPillStyle(op === 'gte')}>Greater than</button>
        <span style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
          {words(shown, op)}
        </span>
      </div>
    </div>
  );
};

// One generic column filter: operator picker + a value box sized to the kind.
const ColumnFilterControl = ({ col, value, onChange }) => {
  const kind = window.columnFilterKind(col);
  const ops = COL_OPS[kind] || COL_OPS.text;
  const f = value && typeof value === 'object' ? value : { op: ops[0][0], value: '' };
  const setOp = (op) => onChange({ op, value: NO_VALUE_OPS.has(op) ? '' : f.value });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <window.Picker value={f.op} onChange={setOp} size="sm" placeholder="Operator" style={{ flexShrink: 0 }}
        options={ops.map(([v, label]) => ({ value: v, label }))} />
      {!NO_VALUE_OPS.has(f.op) && kind === 'number' && (
        <NumberFilterInput value={f.value} placeholder="Amount"
          onCommit={(v) => onChange({ op: f.op, value: v })} />
      )}
      {!NO_VALUE_OPS.has(f.op) && kind === 'date' && (
        <input type="date" value={f.value || ''} onChange={(e) => onChange({ op: f.op, value: e.target.value })}
          style={FILTER_INPUT_STYLE} />
      )}
      {!NO_VALUE_OPS.has(f.op) && kind === 'text' && (
        <input value={f.value || ''} onChange={(e) => onChange({ op: f.op, value: e.target.value })}
          placeholder="Text" style={FILTER_INPUT_STYLE} />
      )}
    </div>
  );
};

// Any-of filter over a long list (HQ state, city, industry) — too many
// options for chips, so the picks show as removable pills over a search box
// and matches appear as you type. Options may carry an `alias` ("GA" for
// Georgia) that matches too. Enter takes the top match; a pasted list
// ("MS, GA, AL") adds every entry it can resolve. `free` fields (city) also
// take typed values that aren't on the list.
const MultiFilterControl = ({ value, options, free, placeholder, onChange }) => {
  const picks = Array.isArray(value) ? value : (value ? [value] : []);
  const [q, setQ] = React.useState('');
  const labelOf = (v) => (options.find((o) => o.value === v) || {}).label || v;
  const resolve = (term) => {
    const t = term.trim().toLowerCase();
    if (!t) return null;
    const hit = options.find((o) => String(o.label).toLowerCase() === t
      || (o.alias && String(o.alias).toLowerCase() === t));
    return hit ? hit.value : (free ? term.trim() : null);
  };
  const needle = q.trim().toLowerCase();
  const matches = !needle || needle.includes(',') ? [] : options
    .filter((o) => !picks.includes(o.value))
    .map((o) => {
      const label = String(o.label).toLowerCase();
      const alias = o.alias ? String(o.alias).toLowerCase() : '';
      const rank = alias === needle ? 0 : label.startsWith(needle) ? 1 : label.includes(needle) ? 2 : -1;
      return { o, rank };
    })
    .filter((m) => m.rank >= 0)
    .sort((a, b) => a.rank - b.rank)
    .slice(0, 8)
    .map((m) => m.o);
  const add = (vals) => {
    const next = [...picks];
    for (const v of vals) if (v && !next.includes(v)) next.push(v);
    if (next.length !== picks.length) onChange(next);
  };
  const remove = (v) => onChange(picks.filter((x) => x !== v));
  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (q.includes(',') || q.includes(';')) {
        const terms = q.split(/[,;]/);
        const left = terms.filter((t) => t.trim() && !resolve(t));
        add(terms.map(resolve));
        setQ(left.join(', ').trim());
      } else if (matches.length) {
        add([matches[0].value]); setQ('');
      } else if (free && q.trim()) {
        add([q.trim()]); setQ('');
      }
    } else if (e.key === 'Backspace' && !q && picks.length) {
      remove(picks[picks.length - 1]);
    }
  };
  return (
    <div>
      {picks.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
          {picks.map((v) => (
            <button key={v} type="button" onClick={() => remove(v)} title="Remove"
              style={{ ...filterPillStyle(true), display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {labelOf(v)}<Icon.Close size={9} />
            </button>
          ))}
        </div>
      )}
      <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown}
        placeholder={placeholder || 'Type to add…'} style={FILTER_INPUT_STYLE} />
      {matches.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
          {matches.map((o) => (
            <button key={o.value} type="button" onClick={() => { add([o.value]); setQ(''); }}
              style={filterPillStyle(false)}>{o.label}</button>
          ))}
        </div>
      )}
    </div>
  );
};

const emptyFilterValue = (s) => (s.type === 'chips' || s.type === 'multi' ? [] : s.type === 'toggle' ? false : '');

window.FilterPopover = ({ filters, setFilters, onClose, spec, anchor = 'right', columns }) => {
  const popRef = React.useRef(null);
  const addInputRef = React.useRef(null);
  const [asyncOptions, setAsyncOptions] = React.useState({});
  // Rows kept on screen while still empty — a just-added Stage filter has
  // no picks yet, and must not vanish before the user makes one.
  const [added, setAdded] = React.useState(() => new Set());
  const [adding, setAdding] = React.useState(false);
  const [q, setQ] = React.useState('');
  const [maxH, setMaxH] = React.useState(null);

  const asyncKeys = spec.filter((s) => typeof s.optionsAsync === 'function').map((s) => s.key).join('|');
  React.useEffect(() => {
    const loaders = spec
      .filter((s) => typeof s.optionsAsync === 'function')
      .map((s) => Promise.resolve(s.optionsAsync()).catch(() => []).then((opts) => [s.key, opts]));
    let alive = true;
    Promise.all(loaders).then((pairs) => {
      if (!alive) return;
      const next = {};
      for (const [k, v] of pairs) next[k] = Array.isArray(v) ? v : ((v && v.items) || []);
      setAsyncOptions(next);
    });
    return () => { alive = false; };
  }, [asyncKeys]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Never taller than the space below the button: the body scrolls instead.
  React.useLayoutEffect(() => {
    if (!popRef.current) return;
    const top = popRef.current.getBoundingClientRect().top;
    setMaxH(Math.max(240, window.innerHeight - top - 16));
  }, []);

  React.useEffect(() => {
    // Combobox menus and the Hot score picker are portaled to <body>, so a
    // click in one lands outside popRef — don't treat it as a click-away.
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)
          && !(e.target.closest && e.target.closest('[data-combobox-menu]'))) onClose();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  React.useEffect(() => { if (adding && addInputRef.current) addInputRef.current.focus(); }, [adding]);

  // Every field the popover can offer: the spec's, then any grid column no
  // spec field already covers.
  const fields = React.useMemo(() => {
    const covered = new Set(spec.map((s) => s.col).filter(Boolean));
    const cols = (columns || [])
      .filter((c) => !c.xskip && !c.noFilter && !covered.has(c.key))
      .map((c) => ({ key: COL_FILTER_PREFIX + c.key, label: c.label, type: 'column', col: c,
                     group: c.group || 'Columns', hint: c.desc }));
    return [...spec, ...cols];
  }, [spec, columns]);

  const visible = fields.filter((f) => added.has(f.key) || window.filterValueActive(filters[f.key]));
  const visibleKeys = new Set(visible.map((f) => f.key));
  const needle = q.trim().toLowerCase();
  const available = fields.filter((f) => !visibleKeys.has(f.key)
    && (!needle || String(f.label).toLowerCase().includes(needle)));

  const addRow = (f) => {
    setAdded((prev) => new Set([...prev, f.key]));
    if (f.type === 'toggle') setFilters((prev) => ({ ...prev, [f.key]: true }));
    else if (f.type === 'column') {
      const op = (COL_OPS[window.columnFilterKind(f.col)] || COL_OPS.text)[0][0];
      setFilters((prev) => ({ ...prev, [f.key]: { op, value: '' } }));
    }
    setAdding(false); setQ('');
  };
  const removeRow = (f) => {
    setAdded((prev) => { const next = new Set(prev); next.delete(f.key); return next; });
    setFilters((prev) => {
      const next = { ...prev };
      if (f.type === 'column') delete next[f.key];
      else next[f.key] = emptyFilterValue(f);
      return next;
    });
    if (f.onPick) f.onPick(null);
  };
  const clearAll = () => {
    setAdded(new Set());
    const next = {};
    for (const s of spec) {
      next[s.key] = emptyFilterValue(s);
      if (s.onPick) s.onPick(null);
    }
    setFilters(next);
  };
  const toggleChip = (key, value) => {
    setFilters((f) => {
      const cur = f[key] || [];
      return { ...f, [key]: cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value] };
    });
  };

  const renderControl = (s) => {
    const options = (s.options || asyncOptions[s.key] || []).map((opt) =>
      typeof opt === 'string' ? { value: opt, label: opt } : opt);
    const val = filters[s.key];
    if (s.type === 'chips') return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {options.map((o) => (
          <button key={o.value} type="button" onClick={() => toggleChip(s.key, o.value)}
            style={filterPillStyle((val || []).includes(o.value))}>{o.label}</button>
        ))}
        {!options.length && <span style={{ fontSize: 11, color: 'var(--text-4)' }}>Loading…</span>}
      </div>
    );
    if (s.type === 'multi') return (
      <MultiFilterControl value={val} options={options} free={s.free} placeholder={s.placeholder}
        onChange={(v) => setFilters((f) => ({ ...f, [s.key]: v }))} />
    );
    if (s.type === 'select' && s.loadOptions) return (
      <window.Combobox
        value={val || ''}
        onChange={(e) => {
          const v = e.target.value;
          setFilters((f) => ({ ...f, [s.key]: v }));
          if (s.onPick) s.onPick(v ? { value: v, label: e.label } : null);
        }}
        loadOptions={s.loadOptions}
        placeholder={s.placeholder || 'Any'}
        options={[
          { value: '', label: s.placeholder || 'Any' },
          // The "Any" clear row is built in — drop caller dupes.
          ...options.filter((o) => o.value != null && o.value !== ''),
        ]}
      />
    );
    if (s.type === 'select') return (
      <window.Picker value={val || ''} width="100%"
        onChange={(v) => setFilters((f) => ({ ...f, [s.key]: v }))}
        emptyLabel={s.placeholder || 'Any'} placeholder={s.placeholder || 'Any'}
        // The "Any" row is built in — drop caller dupes. String values, as
        // the native select this replaced handed back.
        options={options.filter((o) => o.value != null && o.value !== '').map((o) => ({ value: String(o.value), label: o.label }))} />
    );
    if (s.type === 'number') return (
      <NumberFilterInput value={val} list={s.list} placeholder={s.placeholder || ''}
        onCommit={(v) => setFilters((f) => ({ ...f, [s.key]: v }))} />
    );
    if (s.type === 'text') return (
      <input value={val || ''} list={s.list}
        onChange={(e) => setFilters((f) => ({ ...f, [s.key]: e.target.value }))}
        placeholder={s.placeholder || ''} style={FILTER_INPUT_STYLE} />
    );
    if (s.type === 'score') return (
      <ScoreFilterControl value={val} onChange={(v) => setFilters((f) => ({ ...f, [s.key]: v }))} />
    );
    if (s.type === 'column') return (
      <ColumnFilterControl col={s.col} value={val}
        onChange={(v) => setFilters((f) => ({ ...f, [s.key]: v }))} />
    );
    return null;
  };

  const anchorStyle = anchor === 'right' ? { right: 0 } : { left: 0 };
  const removeBtn = (f) => (
    <button type="button" onClick={() => removeRow(f)} title="Remove this filter"
      style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 2, display: 'inline-flex',
               color: 'var(--text-4)', flexShrink: 0 }}>
      <Icon.Close size={11} />
    </button>
  );

  // Add-filter list: spec fields first (no heading), then columns by group.
  const groups = [];
  for (const f of available) {
    const g = f.type === 'column' ? f.group : '';
    let bucket = groups.find((x) => x.name === g);
    if (!bucket) { bucket = { name: g, items: [] }; groups.push(bucket); }
    bucket.items.push(f);
  }

  return (
    <div ref={popRef} style={{
      position: 'absolute', top: 'calc(100% + 6px)', zIndex: 50, ...anchorStyle,
      width: 340, background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 8, boxShadow: 'var(--shadow-lg)', fontSize: 12,
      display: 'flex', flexDirection: 'column', maxHeight: maxH || undefined,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px 8px' }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Filters</span>
        {window.activeFilterCount(filters) > 0 && (
          <button type="button" onClick={clearAll}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--accent-text)' }}>
            Clear all
          </button>
        )}
      </div>

      <div style={{ overflowY: 'auto', padding: '0 12px', flex: '1 1 auto', minHeight: 0 }}>
        {!visible.length && (
          <div style={{ fontSize: 11.5, color: 'var(--text-4)', padding: '2px 0 10px' }}>No filters applied.</div>
        )}
        {visible.map((f) => (
          <div key={f.key} style={{ padding: '8px 0', borderTop: '1px solid var(--border)' }}>
            {f.type === 'toggle' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label title={f.hint || undefined} style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1,
                  fontSize: 12, color: 'var(--text-2)', cursor: 'pointer', minWidth: 0 }}>
                  <input type="checkbox" checked={!!filters[f.key]}
                    onChange={(e) => setFilters((prev) => ({ ...prev, [f.key]: e.target.checked }))} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.label}</span>
                </label>
                {removeBtn(f)}
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                  <span title={f.hint || undefined} style={{ flex: 1, fontSize: 11, fontWeight: 600, color: 'var(--text-2)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.label}</span>
                  {removeBtn(f)}
                </div>
                {renderControl(f)}
              </>
            )}
          </div>
        ))}
      </div>

      <div style={{ borderTop: '1px solid var(--border)', padding: 8 }}>
        {!adding ? (
          <button type="button" onClick={() => setAdding(true)} disabled={!available.length}
            style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '5px 6px',
              background: 'transparent', border: 'none', borderRadius: 5, cursor: available.length ? 'pointer' : 'default',
              fontSize: 12, color: available.length ? 'var(--accent-text)' : 'var(--text-4)', fontFamily: 'inherit' }}>
            <Icon.Plus size={12} /> Add filter
          </button>
        ) : (
          <div>
            <input ref={addInputRef} value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Find a field or column…"
              onKeyDown={(e) => {
                if (e.key === 'Escape') { e.stopPropagation(); setAdding(false); setQ(''); }
                else if (e.key === 'Enter' && available[0]) { e.preventDefault(); addRow(available[0]); }
              }}
              style={FILTER_INPUT_STYLE} />
            <div style={{ maxHeight: 220, overflowY: 'auto', marginTop: 6 }}>
              {groups.map((g) => (
                <div key={g.name || '_'}>
                  {g.name && (
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase',
                      letterSpacing: '0.06em', padding: '6px 6px 2px' }}>{g.name}</div>
                  )}
                  {g.items.map((f) => (
                    <button key={f.key} type="button" onClick={() => addRow(f)} title={f.hint || undefined}
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '5px 6px', borderRadius: 4,
                        background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12,
                        color: 'var(--text)', fontFamily: 'inherit' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-hover)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                      {f.label}
                    </button>
                  ))}
                </div>
              ))}
              {!available.length && (
                <div style={{ fontSize: 11.5, color: 'var(--text-4)', padding: '6px' }}>Nothing matches.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// CSV / JSON export — client-side, no backend. The rows are already in hand
// (a chart/table result or any list). columns: [{ key, label }]; rows: [{...}].
// Proper quote-escaping so commas/quotes/newlines in cell values survive.
const _downloadBlob = (blob, filename, fallback) => {
  // The iOS shell's WKWebView ignores <a download>: write the file and open
  // the share sheet instead. Returns that promise (web returns undefined).
  if (window.AtlasNative && window.AtlasNative.isNative) {
    return window.AtlasNative.saveFile(blob, filename || fallback, blob && blob.type).catch((e) => {
      if (window.pushToast) window.pushToast({ type: 'error', title: 'Could not save file', body: (e && e.message) || String(e) });
    });
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || fallback;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
// Shared with api.jsx's server-side export helpers (a plain <a href> to the
// export endpoints can't carry the auth header — they fetch a blob instead).
window.downloadBlob = _downloadBlob;

// ────────────────────────────────────────────────────────────────────────
// Export driven by the user's visible columns.
//
// Each grid used to keep a second, hand-maintained EXPORT_COLUMNS list, so
// the Columns menu had no effect on the file: hiding six columns still
// exported them, and the opt-in columns couldn't be exported at all. Export
// now follows what you see, in the order you put it.
//
// What it must NOT lose is why that list existed. Export values are RAW —
// unformatted numbers and ISO dates, so Excel and scripts can compute
// instead of parsing "$1.1M" — and some grid cells carry more than one fact
// (Revenue also records whether the figure came from the engagement or the
// company estimate). So a catalog column may declare:
//
//   xget(row)   raw export value; defaults to `accessor` (the display text)
//   xextra      [{ key, label, get }] — extra columns emitted right after
//               this one, only when this column is visible
//   xskip       true for columns with nothing meaningful to export
//               (a progress bar, a row-action affordance)
//
// `always` are identifier columns appended regardless of layout — the Atlas
// id, so an exported file can be joined back.
window.exportColumnsFor = (cols, always) => {
  const out = [];
  // Export keys become JSON object keys, so they must be unique or a field
  // silently overwrites another. Grid keys are unique among themselves, but
  // xextra/always keys and de-namespaced formula keys can collide with them
  // (a custom field literally named "owner"), so suffix any repeat.
  const seen = new Set();
  const uniq = (k) => {
    let key = k, n = 2;
    while (seen.has(key)) key = `${k}_${n++}`;
    seen.add(key);
    return key;
  };
  const push = (key, label, get) => out.push({ key: uniq(key), label, get });

  for (const c of (cols || [])) {
    if (c.xskip) continue;
    // xkey lets a column export under a friendlier name than its grid key —
    // formula columns drop their `f:` namespace prefix here.
    push(c.xkey || c.key, c.label, c.xget || c.accessor || ((r) => r[c.key]));
    for (const e of (c.xextra || [])) push(e.key, e.label, e.get);
  }
  for (const e of (always || [])) push(e.key, e.label, e.get);
  return out;
};

// Rows → flat records shaped by exportColumnsFor's list. A getter that throws
// on an odd row yields an empty cell rather than failing the whole export.
window.exportRecords = (columns, rows) => (rows || []).map((r) => {
  const rec = {};
  for (const c of columns) {
    let v;
    try { v = c.get(r); } catch (_) { v = ''; }
    rec[c.key] = v == null ? '' : v;
  }
  return rec;
});

window.exportCsv = (filename, columns, rows) => {
  const esc = (v) => {
    if (v == null) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const head = columns.map((c) => esc(c.label || c.key)).join(',');
  const body = (rows || []).map((r) => columns.map((c) => esc(r[c.key])).join(',')).join('\n');
  // BOM so Excel detects UTF-8 — accented company/contact names garble
  // without it (Sheets and text editors ignore it).
  const csv = '\ufeff' + `${head}\n${body}`;
  _downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), filename, 'export.csv');
};

// JSON sibling — same rows-in-hand contract, for scripts instead of Excel.
window.exportJson = (filename, rows) => {
  const blob = new Blob([JSON.stringify(rows ?? [], null, 2)], { type: 'application/json' });
  _downloadBlob(blob, filename, 'export.json');
};

// Export button + CSV/JSON menu, shared by every list view so the affordance
// reads the same everywhere. The view supplies the numbers and does the work:
//   selectedCount  rows checked in the grid (0/null → whole-view export)
//   viewCount      what a whole-view export would hold (display only)
//   capNote        e.g. "first 10,000 by current sort" when the view-wide
//                  fetch tops out at the server cap — say so rather than let
//                  a truncated file read as complete
//   onExport(fmt)  'csv' | 'json'; may be async (button shows Exporting…)
window.ExportMenu = ({ disabled, selectedCount, viewCount, capNote, onExport }) => {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const run = async (fmt) => {
    setOpen(false);
    setBusy(true);
    try { await onExport(fmt); } finally { setBusy(false); }
  };
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <Btn size="sm" variant="outline" disabled={disabled || busy} onClick={() => setOpen((v) => !v)}>
        {busy ? 'Exporting…' : `Export${selectedCount ? ` (${selectedCount.toLocaleString()})` : ''}`}
      </Btn>
      {open && (
        <div role="menu" style={{
          position: 'absolute', top: 30, right: 0, zIndex: 60, minWidth: 230,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 8, boxShadow: 'var(--shadow-lg)', overflow: 'hidden',
        }}>
          <div style={{
            padding: '8px 12px', fontSize: 10, fontWeight: 700,
            color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em',
            borderBottom: '1px solid var(--border)', background: 'var(--surface-2)',
          }}>
            {selectedCount
              ? `${selectedCount.toLocaleString()} selected row${selectedCount === 1 ? '' : 's'}`
              : `This view${viewCount != null ? ` (${viewCount.toLocaleString()} rows)` : ''}`}
            {!selectedCount && capNote && (
              <div style={{ marginTop: 2, textTransform: 'none', letterSpacing: 0, fontWeight: 500, color: 'var(--amber-fg)' }}>{capNote}</div>
            )}
          </div>
          {[
            ['csv',  'CSV',  'Opens in Excel / Google Sheets.'],
            ['json', 'JSON', 'Raw records, for scripts.'],
          ].map(([fmt, label, sub], i) => (
            <button key={fmt} onClick={() => run(fmt)} style={{
              display: 'block', width: '100%', padding: '9px 12px', textAlign: 'left',
              background: 'var(--surface)', border: 'none',
              borderBottom: i === 0 ? '1px solid var(--border)' : 'none', cursor: 'pointer',
            }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'var(--surface)'}
            >
              <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{label}</div>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{sub}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Server-side CSV export dialog with a column picker. Unlike exportCsv above
// (rows already in hand, capped at the run's 2,000 rows and silently
// truncated), this re-runs the query on the server and downloads up to
// 10,000 rows. columns: [{key, label}]. Id-like columns (`id`, `*_id`) are
// listed but UNCHECKED by default — raw UUIDs stay out of spreadsheets unless
// asked for (override which keys start off via `defaultOff(key) => bool`).
// onExport(selectedCols) does the fetch (see AtlasApi.analytics.exportCsv et
// al) and must resolve to {truncated} — hitting the cap surfaces as a toast.
// With no known columns yet (e.g. a widget exported before its first load),
// the picker is skipped and onExport(null) exports every result column.
window.ExportDialog = ({ title, columns, defaultOff, onExport, onClose, zIndex }) => {
  const isOff = defaultOff || ((key) => /(^id$|_id$)/i.test(key));
  const cols = columns || [];
  const [checked, setChecked] = React.useState(() => new Set(cols.filter((c) => !isOff(c.key)).map((c) => c.key)));
  const [busy, setBusy] = React.useState(false);
  const toggle = (key) => setChecked((s) => {
    const next = new Set(s);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const allOn = cols.length > 0 && checked.size === cols.length;
  const run = async () => {
    setBusy(true);
    try {
      const sel = cols.length
        ? cols.filter((c) => checked.has(c.key)).map((c) => ({ key: c.key, label: c.label || c.key }))
        : null;   // no column list known → server exports all result columns
      const res = await onExport(sel);
      if (res && res.truncated) {
        window.pushToast({ type: 'warning', title: 'Export truncated', body: 'The query returned more than 10,000 rows — the file holds the first 10,000.' });
      }
      onClose();
    } catch (e) {
      window.pushToast({ type: 'error', title: 'Export failed', body: (e && e.message) || String(e) });
      setBusy(false);
    }
  };
  return (
    <window.Drawer open onClose={onClose} title="Export CSV" subtitle={title} width={400} zIndex={zIndex}
      footer={<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
        <window.Btn size="sm" variant="ghost" onClick={onClose} disabled={busy}>Cancel</window.Btn>
        <window.Btn size="sm" variant="accent" onClick={run} disabled={busy || (cols.length > 0 && checked.size === 0)}>
          {busy ? 'Exporting…' : 'Download CSV'}
        </window.Btn>
      </div>}>
      {cols.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-3)', lineHeight: 1.5 }}>
          All result columns will be exported.
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Columns · {checked.size}/{cols.length}
            </span>
            <button onClick={() => setChecked(allOn ? new Set() : new Set(cols.map((c) => c.key)))}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11, color: 'var(--accent-text)' }}>
              {allOn ? 'Clear all' : 'Select all'}
            </button>
          </div>
          <div style={{ maxHeight: 360, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {cols.map((c) => (
              <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px', borderRadius: 6, cursor: 'pointer', fontSize: 12.5, color: 'var(--text)' }}>
                <input type="checkbox" checked={checked.has(c.key)} onChange={() => toggle(c.key)} style={{ accentColor: 'var(--accent)' }} />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.label || c.key}</span>
                {(c.label && c.label !== c.key) && (
                  <span style={{ fontSize: 10, color: 'var(--text-4)' }} title="Result column">{c.key}</span>
                )}
              </label>
            ))}
          </div>
        </>
      )}
    </window.Drawer>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Windowed row rendering
//
// The grids paginate from the server but keep every loaded row in the DOM,
// so a deep scroll through Companies/People accumulates thousands of row
// nodes and every frame pays for them. WindowedRows renders only the rows
// near the viewport (plus `overscan` on each side) between two spacer divs
// that preserve the scroll geometry, so the sticky headers, the
// IntersectionObserver sentinel below, and the scrollbar all behave as if
// the full list were there.
//
// Constraints, by design:
// - rows must be uniform height (`rowHeight` is just the initial estimate —
//   the real height is measured from the rendered slice on every update,
//   so it self-corrects for zoom/density; per-row variation is NOT ok);
// - `renderRow` must emit exactly one DOM element per row;
// - below `threshold` rows it renders a plain map — zero behavior change
//   for small lists, and callers with variable-height modes (grouped or
//   expanded rows) should simply not route those through this component.
//
// The scroll container is found by walking up from the anchor to the first
// overflow-y auto/scroll ancestor, so views keep owning their own layout.
window.WindowedRows = ({ rows, renderRow, rowHeight = 39, overscan = 15, threshold = 200 }) => {
  const anchorRef = React.useRef(null);
  const rhRef = React.useRef(rowHeight);
  const active = rows.length > threshold;
  const [win, setWin] = React.useState({ start: 0, end: threshold, rh: rowHeight });
  React.useLayoutEffect(() => {
    if (!active) return;
    const anchor = anchorRef.current;
    if (!anchor) return;
    let scroller = anchor.parentElement;
    while (scroller && scroller !== document.documentElement) {
      const oy = getComputedStyle(scroller).overflowY;
      if (oy === 'auto' || oy === 'scroll') break;
      scroller = scroller.parentElement;
    }
    if (scroller === document.documentElement) scroller = null;
    let raf = 0;
    const update = () => {
      raf = 0;
      // Re-measure the real row height from the current slice: the pixel
      // span from the first rendered row to the bottom spacer, over the
      // number of rendered rows. children = [topSpacer, ...rows, bottomSpacer].
      const kids = anchor.children;
      const rendered = kids.length - 2;
      if (rendered > 0) {
        const span = kids[kids.length - 1].getBoundingClientRect().top - kids[1].getBoundingClientRect().top;
        if (span / rendered > 8) rhRef.current = span / rendered;
      }
      const rh = rhRef.current;
      const viewTop = scroller ? scroller.getBoundingClientRect().top : 0;
      const viewH = scroller ? scroller.clientHeight : window.innerHeight;
      const listTop = anchor.getBoundingClientRect().top - viewTop;
      const start = Math.max(0, Math.min(rows.length - 1, Math.floor(-listTop / rh) - overscan));
      const end = Math.min(rows.length, start + Math.ceil(viewH / rh) + overscan * 2);
      setWin((w) => (w.start === start && w.end === end && w.rh === rh ? w : { start, end, rh }));
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    const tgt = scroller || window;
    tgt.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      tgt.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [active, rows.length, overscan]);
  if (!active) return rows.map(renderRow);
  return (
    <div ref={anchorRef}>
      <div style={{ height: win.start * win.rh }} />
      {rows.slice(win.start, win.end).map(renderRow)}
      <div style={{ height: Math.max(0, rows.length - win.end) * win.rh }} />
    </div>
  );
};

// ============ STICKY OWNER SCOPE (per-view "mine by default") ============
// Grids with an Owner filter (Referral Partners, GPP Partners, Referrals)
// open on the signed-in user's own rows, mirroring the Engagements Mine/Team
// switch (deals.jsx AtlasEngScope): a role-seeded first-run default, and an
// explicit flip to "Any owner" — or back to yourself — is remembered per
// view. Picking a COLLEAGUE stays a one-visit peek; only mine/any persist.
window.makeOwnerScope = (storageKey) => {
  const valid = (v) => v === 'mine' || v === 'any';
  const scope = {
    // Dealmakers and senior associates own a book → mine. Associates,
    // marketing, and admins work cross-team lists → any (associates saw
    // "almost nothing" under Mine on Engagements — same failure mode here).
    defaultFor: (role) => {
      const r = (role || '').toLowerCase();
      return (r === 'associate' || r === 'marketing' || r === 'admin') ? 'any' : 'mine';
    },
    hasStored: () => {
      try { return valid(window.localStorage.getItem(storageKey)); } catch { return false; }
    },
    get: (role) => {
      // The header's All-teams toggle is an explicit "show me everything" —
      // don't pin a firm-wide view back to the viewer's own rows.
      if (window.AtlasApi?.isAllTeams?.()) return 'any';
      try {
        const v = window.localStorage.getItem(storageKey);
        if (valid(v)) return v;
      } catch {}
      return scope.defaultFor(role);
    },
    set: (s) => { try { window.localStorage.setItem(storageKey, s); } catch {} },
  };
  return scope;
};
